import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  Archive,
  ArrowRight,
  Check,
  Download as DownloadIcon,
  Eye,
  FileCheck,
  Flame,
  Lock,
  Play,
  RotateCcw,
  ShieldAlert,
  UploadCloud,
  Zap,
} from "lucide-react";
import { isLscError, type Fragment } from "@limeshare/lsc1";
import { ApiClientError, api } from "../api";
import { CryptoCheckoutModal } from "../components/CryptoCheckoutModal";
import { FileIcon, getFileCategory } from "../components/FileIcon";
import { MediaModal, type PreviewItem } from "../components/MediaModal";
import { ABUSE_EMAIL } from "../config";
import {
  NeedsPasswordError,
  chooseSink,
  downloadFile,
  openTransfer,
  readFragment,
  reportDownloadComplete,
  type OpenedFile,
  type OpenedTransfer,
} from "../engine/download";
import { SpeedTracker, type TransferStats } from "../engine/speed";
import { releaseWakeLock, requestWakeLock } from "../engine/wakelock";
import { downloadAllAsZip, estimateZipSize, type ZipProgress } from "../engine/zip";
import { errorMessage, formatBytes, formatDuration, safeFileName } from "../util";

type State =
  | { kind: "loading" }
  | { kind: "password"; fragment: Extract<Fragment, { kind: "password" }>; error: string | null }
  | { kind: "ready"; transfer: OpenedTransfer }
  | { kind: "error"; title: string; message: string };

interface FileItemState {
  status: "idle" | "busy" | "done" | "error";
  bytesDone: number;
  bytesTotal: number;
  stats?: TransferStats;
  message?: string;
}

interface ZipDownloadState {
  status: "idle" | "busy" | "done" | "error";
  progress?: ZipProgress;
  stats?: TransferStats;
  message?: string;
}

export function Download() {
  const { id = "" } = useParams();
  const [state, setState] = useState<State>({ kind: "loading" });
  const [fileStates, setFileStates] = useState<Record<string, FileItemState>>({});
  const [zipState, setZipState] = useState<ZipDownloadState>({ status: "idle" });
  const [previewItem, setPreviewItem] = useState<PreviewItem | null>(null);
  const [previewLoadingId, setPreviewLoadingId] = useState<string | null>(null);
  const [rescueOpen, setRescueOpen] = useState(false);
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);
  const [paywallUnlockedLocal, setPaywallUnlockedLocal] = useState(false);
  const [customExpiry, setCustomExpiry] = useState<number | null>(null);
  const [rescued, setRescued] = useState(false);
  const [stealthCamouflage, setStealthCamouflage] = useState(false);

  const fragmentRef = useRef<Fragment | null>(null);
  const speedTrackers = useRef<Record<string, SpeedTracker>>({});
  const zipSpeedTracker = useRef(new SpeedTracker());

  const open = async (password?: string) => {
    try {
      const fragment = fragmentRef.current ?? readFragment();
      fragmentRef.current = fragment;
      const transfer = await openTransfer(id, fragment, password);
      setState({ kind: "ready", transfer });
    } catch (e) {
      if (e instanceof NeedsPasswordError) {
        setState({ kind: "password", fragment: e.fragment, error: null });
        return;
      }
      if (isLscError(e, "WRONG_PASSWORD_OR_CORRUPT") && fragmentRef.current?.kind === "password") {
        setState({ kind: "password", fragment: fragmentRef.current, error: "Wrong password." });
        return;
      }
      setState(describeError(e));
    }
  };

  useEffect(() => {
    void open();
  }, [id]);

  const updateFileState = (fileId: string, patch: Partial<FileItemState>) => {
    setFileStates((prev) => ({
      ...prev,
      [fileId]: { ...(prev[fileId] ?? { status: "idle", bytesDone: 0, bytesTotal: 0 }), ...patch },
    }));
  };

  const handleDownloadSingle = async (t: OpenedTransfer, f: OpenedFile) => {
    const ext = f.meta.name.includes(".") ? "." + f.meta.name.split(".").pop() : ".bin";
    const targetName = stealthCamouflage ? `report_2026_${f.fileId.slice(0, 4)}${ext}` : safeFileName(f.meta.name);
    let sink;
    try {
      sink = await chooseSink(targetName, f.meta.size);
    } catch (e) {
      if ((e as Error)?.name !== "AbortError") {
        updateFileState(f.fileId, { status: "error", message: errorMessage(e) });
      }
      return;
    }

    if (!speedTrackers.current[f.fileId]) {
      speedTrackers.current[f.fileId] = new SpeedTracker();
    }
    const tracker = speedTrackers.current[f.fileId]!;
    tracker.start();
    void requestWakeLock();

    updateFileState(f.fileId, { status: "busy", bytesDone: 0, bytesTotal: f.meta.size });

    try {
      await downloadFile(t, f, sink, (p) => {
        const stats = tracker.update(p.bytesDone, p.bytesTotal);
        updateFileState(f.fileId, { status: "busy", bytesDone: p.bytesDone, bytesTotal: p.bytesTotal, stats });
      });
      updateFileState(f.fileId, { status: "done" });

      const allDone = t.files.every((x) => x.fileId === f.fileId || fileStates[x.fileId]?.status === "done");
      if (allDone) await reportDownloadComplete(t);
    } catch (e) {
      updateFileState(f.fileId, { status: "error", message: friendlyDecryptError(e) });
    } finally {
      void releaseWakeLock();
    }
  };

  const handleDownloadAllZip = async (t: OpenedTransfer) => {
    const estimated = estimateZipSize(t.files);
    let sink;
    try {
      sink = await chooseSink(`limeshare-${t.transferId}.zip`, estimated);
    } catch (e) {
      if ((e as Error)?.name !== "AbortError") {
        setZipState({ status: "error", message: errorMessage(e) });
      }
      return;
    }

    zipSpeedTracker.current.start();
    setZipState({ status: "busy" });
    void requestWakeLock();

    try {
      await downloadAllAsZip(t, sink, (p) => {
        const stats = zipSpeedTracker.current.update(p.bytesDone, p.bytesTotal);
        setZipState({ status: "busy", progress: p, stats });
      });
      setZipState({ status: "done" });
      await reportDownloadComplete(t);
    } catch (e) {
      setZipState({ status: "error", message: friendlyDecryptError(e) });
    } finally {
      void releaseWakeLock();
    }
  };

  const handlePreview = async (t: OpenedTransfer, f: OpenedFile) => {
    if (f.meta.size > 256 * 1024 * 1024) {
      alert("Files larger than 256 MB cannot be previewed directly in browser RAM. Please use Download instead.");
      return;
    }

    setPreviewLoadingId(f.fileId);
    try {
      const chunks: Uint8Array[] = [];
      const memorySink = {
        kind: "memory" as const,
        write: async (stream: ReadableStream<Uint8Array>) => {
          const reader = stream.getReader();
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            chunks.push(value);
          }
        },
        abort: async () => undefined,
      };

      await downloadFile(t, f, memorySink, () => undefined);

      const mime = f.meta.type || "application/octet-stream";
      const blob = new Blob(chunks, { type: mime });
      setPreviewItem({
        fileId: f.fileId,
        transferId: t.transferId,
        token: t.token,
        name: f.meta.name,
        size: f.meta.size,
        type: mime,
        blob,
      });
    } catch (e) {
      alert(`Preview failed: ${friendlyDecryptError(e)}`);
    } finally {
      setPreviewLoadingId(null);
    }
  };

  if (state.kind === "loading") {
    return (
      <div className="center">
        <div className="brand dot" style={{ margin: "0 auto 16px", width: 24, height: 24 }} />
        Opening encrypted zero-knowledge vault…
      </div>
    );
  }

  if (state.kind === "password") {
    return <PasswordPrompt error={state.error} onSubmit={(pw) => open(pw)} />;
  }

  if (state.kind === "error") {
    return (
      <section className="panel">
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#ff5c5c", marginBottom: 8 }}>
          <ShieldAlert size={24} />
          <h2 style={{ margin: 0 }}>{state.title}</h2>
        </div>
        <p className="hint">{state.message}</p>
        <p className="hint" style={{ marginTop: 20 }}>
          <a href="/" className="btn ghost btn-sm">
            Send your own files
          </a>
        </p>
      </section>
    );
  }

  const t = state.transfer;
  const total = t.files.reduce((s, f) => s + f.meta.size, 0);
  const isPaywalled = Boolean(t.paywallPriceUsd && t.paywallPriceUsd >= 2 && !t.paywallUnlocked && !paywallUnlockedLocal);

  return (
    <>
      <section className="panel glow">
        {t.manifest.sender && (
          <div style={{ marginBottom: 14 }}>
            <span className="badge">
              <FileCheck size={13} color="var(--lime)" /> ✓ Verified Delivery by {t.manifest.sender}
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Lock size={18} color="#00ff66" />
              <h2 style={{ margin: 0, fontSize: 22 }}>
                {t.files.length} encrypted file{t.files.length === 1 ? "" : "s"} · {formatBytes(total)}
              </h2>
            </div>
            <p className="hint" style={{ margin: "4px 0 0" }}>
              Decrypted directly in your browser. {t.expiresAt ? `Expires in ${formatDuration(t.expiresAt - Date.now())}.` : ""}
              {t.downloadsRemaining !== null ? ` ${t.downloadsRemaining} pull(s) left.` : ""}
            </p>
          </div>

          {t.files.length > 1 && (
            <button
              className="btn"
              disabled={zipState.status === "busy"}
              onClick={() => (isPaywalled ? setPaywallModalOpen(true) : handleDownloadAllZip(t))}
            >
              <Archive size={16} />
              <span>
                {isPaywalled
                  ? `Unlock All ($${t.paywallPriceUsd?.toFixed(2)})`
                  : zipState.status === "busy"
                    ? "Building ZIP…"
                    : "Download all (ZIP)"}
              </span>
            </button>
          )}
        </div>

        {isPaywalled && (
          <div className="alert warn" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginTop: 16 }}>
            <div>
              <b>Escrow / Pay-to-Decrypt Delivery (${t.paywallPriceUsd?.toFixed(2)} USD):</b> You can inspect file sizes and preview media below. Unlock full master download with a 1-click payment.
            </div>
            <button className="btn btn-sm" onClick={() => setPaywallModalOpen(true)}>
              <Zap size={14} /> Unlock Download (${t.paywallPriceUsd?.toFixed(2)})
            </button>
          </div>
        )}

        {zipState.status === "busy" && (
          <div className="progress" style={{ marginTop: 16 }}>
            <div className="bar">
              <div style={{ width: `${zipState.stats?.percent ?? 0}%` }} />
            </div>
            <div className="meta">
              <span>Streaming ZIP: {zipState.progress?.fileName} ({zipState.progress ? zipState.progress.fileIndex + 1 : 0}/{t.files.length})</span>
              <div className="progress-stats">
                {zipState.stats?.speedFormatted && <span>{zipState.stats.speedFormatted}</span>}
                <span>{zipState.stats?.percent ?? 0}%</span>
              </div>
            </div>
          </div>
        )}

        {zipState.status === "error" && (
          <div className="alert error">{zipState.message}</div>
        )}

        {t.manifest.message && (
          <div className="alert ok" style={{ whiteSpace: "pre-wrap", marginTop: 16 }}>
            <b>Note from sender:</b> {t.manifest.message}
          </div>
        )}

        {t.burn && (
          <div className="alert warn" style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16 }}>
            <Flame size={20} color="#ffb84d" style={{ flexShrink: 0 }} />
            <span>
              <b>Burn-on-read active:</b> This package will be permanently purged from cloud storage right after you complete this download session.
            </span>
          </div>
        )}

        <ul className="files" style={{ marginTop: 20 }}>
          {t.files.map((f) => {
            const fs = fileStates[f.fileId] ?? { status: "idle", bytesDone: 0, bytesTotal: f.meta.size };
            const { category } = getFileCategory(f.meta.name, f.meta.type);
            const canPreview = ["image", "video", "audio", "code", "document"].includes(category) && f.meta.size <= 256 * 1024 * 1024;
            const isPreviewLoading = previewLoadingId === f.fileId;

            return (
              <li key={f.fileId} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div className="file">
                  <div className="file-info">
                    <FileIcon name={f.meta.name} type={f.meta.type} size={20} />
                    <span className="name" title={f.meta.name}>
                      {safeFileName(f.meta.name)}
                    </span>
                    <span className="size">{formatBytes(f.meta.size)}</span>
                  </div>

                  <div className="file-actions">
                    {canPreview && (
                      <button
                        className="btn ghost btn-sm"
                        disabled={fs.status === "busy" || isPreviewLoading}
                        onClick={() => handlePreview(t, f)}
                        title="Preview in browser without saving"
                      >
                        {isPreviewLoading ? (
                          <span>Decrypting…</span>
                        ) : category === "video" || category === "audio" ? (
                          <>
                            <Play size={13} /> <span>Play</span>
                          </>
                        ) : (
                          <>
                            <Eye size={13} /> <span>Preview</span>
                          </>
                        )}
                      </button>
                    )}

                    <button
                      className="btn btn-sm"
                      disabled={fs.status === "busy"}
                      onClick={() => (isPaywalled ? setPaywallModalOpen(true) : handleDownloadSingle(t, f))}
                    >
                      {fs.status === "busy" ? (
                        <span>{fs.stats?.percent ?? 0}%</span>
                      ) : fs.status === "done" ? (
                        <>
                          <Check size={14} color="#041206" /> <span>Saved</span>
                        </>
                      ) : isPaywalled ? (
                        <>
                          <Lock size={14} /> <span>Unlock (${t.paywallPriceUsd?.toFixed(2)})</span>
                        </>
                      ) : (
                        <>
                          <DownloadIcon size={14} /> <span>Decrypt &amp; save</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {fs.status === "busy" && (
                  <div className="progress" style={{ marginTop: 0 }}>
                    <div className="bar">
                      <div style={{ width: `${fs.stats?.percent ?? 0}%` }} />
                    </div>
                    <div className="meta">
                      <span>Decrypting to disk…</span>
                      <div className="progress-stats">
                        {fs.stats?.speedFormatted && <span>{fs.stats.speedFormatted}</span>}
                        {fs.stats?.etaFormatted && fs.stats.etaFormatted !== "—" && <span>ETA: {fs.stats.etaFormatted}</span>}
                        <span>{fs.stats?.percent ?? 0}%</span>
                      </div>
                    </div>
                  </div>
                )}

                {fs.status === "error" && <div className="alert error" style={{ marginTop: 0 }}>{fs.message}</div>}
              </li>
            );
          })}
        </ul>

        <div className="footer" style={{ paddingTop: 24, borderTop: "1px solid var(--border)", marginTop: 28 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, width: "100%" }}>
            <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
              <ReportLink transferId={t.transferId} />
              <label className="check" style={{ fontSize: 12 }}>
                <input type="checkbox" checked={stealthCamouflage} onChange={(e) => setStealthCamouflage(e.target.checked)} />
                <span>Stealth Camouflage Filename (report_2026…)</span>
              </label>
            </div>
            {!rescued ? (
              <button className="btn ghost btn-sm" style={{ borderColor: "rgba(0,255,102,0.4)", color: "var(--lime)" }} onClick={() => setRescueOpen(true)}>
                <Zap size={13} /> Need more time? Freeze Timer &amp; Keep 1 Year ($1.99)
              </button>
            ) : (
              <span className="badge">
                <Check size={12} color="#00ff66" /> Timer Frozen — Kept until {new Date(customExpiry ?? Date.now() + 365 * 86_400_000).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Viral Loop: Send files back section */}
      <section className="panel" style={{ marginTop: 22, textAlign: "center", borderStyle: "dashed" }}>
        <div style={{ maxWidth: 500, margin: "0 auto" }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(0,255,102,0.1)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#00ff66", marginBottom: 10 }}>
            <UploadCloud size={24} />
          </div>
          <h3 style={{ margin: "0 0 6px", fontSize: 18 }}>Need to send files back?</h3>
          <p className="hint" style={{ margin: "0 0 16px" }}>
            LimeShare lets you transfer up to 5 GB free with end-to-end client encryption. No registration, zero logs, auto-purged.
          </p>
          <a href="/" className="btn">
            Send files for free <ArrowRight size={14} />
          </a>
        </div>
      </section>

      {paywallModalOpen && (
        <CryptoCheckoutModal
          defaultSku="paywall-unlock"
          allowedSkus={["paywall-unlock"]}
          transferId={t.transferId}
          title={`Unlock Master Files ($${(t.paywallPriceUsd ?? 15).toFixed(2)} USD)`}
          subtitle="Complete the creator's Pay-to-Decrypt escrow payment to unlock full uncompressed downloads immediately."
          onSuccess={(code) => {
            api.extend(t.transferId, { passCode: code })
              .then(() => setPaywallUnlockedLocal(true))
              .catch(() => setPaywallUnlockedLocal(true));
            setPaywallModalOpen(false);
          }}
          onClose={() => setPaywallModalOpen(false)}
        />
      )}

      {rescueOpen && (
        <CryptoCheckoutModal
          defaultSku="rescue-1y"
          allowedSkus={["rescue-1y", "extend-6m", "vault-pass-15"]}
          transferId={t.transferId}
          title="Recipient Rescue — Freeze Expiration Timer (1 Year)"
          subtitle="Can't download right now? Freeze the self-destruct timer and keep these encrypted files online for 365 days."
          onSuccess={(code) => {
            api.extend(t.transferId, { passCode: code })
              .then((r) => {
                setCustomExpiry(r.expiresAt);
                setRescued(true);
              })
              .catch(() => setRescued(true));
            setRescueOpen(false);
          }}
          onClose={() => setRescueOpen(false)}
        />
      )}

      {/* Media Preview Modal */}
      {previewItem && (
        <MediaModal
          item={previewItem}
          onClose={() => setPreviewItem(null)}
          onDownload={() => {
            const f = t.files.find((x) => x.meta.name === previewItem.name);
            if (f) handleDownloadSingle(t, f);
            setPreviewItem(null);
          }}
        />
      )}
    </>
  );
}

function PasswordPrompt({ error, onSubmit }: { error: string | null; onSubmit: (pw: string) => void }) {
  const [pw, setPw] = useState("");
  return (
    <section className="panel pw glow">
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Lock size={22} color="#00ff66" />
        <h2 style={{ margin: 0 }}>Protected vault</h2>
      </div>
      <p className="hint">This package was sealed with an Argon2id password by the sender. The server never receives it.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(pw);
        }}
      >
        <input type="password" autoFocus placeholder="Enter decryption password" value={pw} onChange={(e) => setPw(e.target.value)} />
        {error && <div className="alert error">{error}</div>}
        <button className="btn block" type="submit" disabled={pw.length === 0} style={{ marginTop: 12 }}>
          Unlock &amp; decrypt
        </button>
      </form>
    </section>
  );
}

function ReportLink({ transferId }: { transferId: string }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<"csam" | "malware" | "copyright" | "personal_data" | "violence" | "other">("copyright");
  const [details, setDetails] = useState("");
  const [contact, setContact] = useState("");
  const [state, setState] = useState<"idle" | "busy" | "sent" | "error">("idle");
  const [caseId, setCaseId] = useState("");

  if (!open) {
    return (
      <button className="btn ghost btn-sm" onClick={() => setOpen(true)}>
        <AlertTriangle size={13} /> Report abuse
      </button>
    );
  }

  const submit = async () => {
    setState("busy");
    try {
      const r = await api.report(transferId, {
        category,
        ...(details.trim() ? { details: details.trim() } : {}),
        ...(contact.trim() ? { contact: contact.trim() } : {}),
      });
      setCaseId(r.caseId);
      setState("sent");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="options" style={{ width: "100%", marginTop: 12 }}>
      <b>Report this transfer</b>
      <p className="hint" style={{ margin: 0 }}>
        We cannot see file contents. CSAM reports lead to immediate storage deletion. Fallback: <a href={`mailto:${ABUSE_EMAIL}?subject=Report%20${encodeURIComponent(transferId)}`}>{ABUSE_EMAIL}</a>
      </p>
      {state === "sent" ? (
        <div className="alert ok" style={{ marginTop: 0 }}>
          Thank you. Case ID <code>{caseId}</code> registered.
        </div>
      ) : (
        <>
          <label className="check">
            Reason
            <select className="select" value={category} onChange={(e) => setCategory(e.target.value as typeof category)}>
              <option value="copyright">Copyright infringement</option>
              <option value="csam">Child sexual abuse material</option>
              <option value="malware">Malware / phishing</option>
              <option value="personal_data">Personal data published without consent</option>
              <option value="violence">Violence / terrorism</option>
              <option value="other">Other</option>
            </select>
          </label>
          <textarea className="textarea" rows={3} maxLength={2000} placeholder="Details (what, where, who you are)" value={details} onChange={(e) => setDetails(e.target.value)} />
          <input className="textinput" style={{ marginTop: 0 }} maxLength={200} placeholder="Your email or contact (optional)" value={contact} onChange={(e) => setContact(e.target.value)} />
          {state === "error" && <div className="alert error" style={{ marginTop: 0 }}>Could not submit report.</div>}
          <div className="row" style={{ marginTop: 0 }}>
            <button className="btn danger btn-sm" disabled={state === "busy"} onClick={submit}>
              Submit report
            </button>
            <button className="btn ghost btn-sm" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function describeError(e: unknown): Extract<State, { kind: "error" }> {
  if (isLscError(e, "FRAGMENT_MISSING") || isLscError(e, "BAD_FRAGMENT")) {
    return {
      kind: "error",
      title: "Broken link",
      message: "The decryption key after # is missing or truncated. Ask the sender to re-copy the full link.",
    };
  }
  if (e instanceof ApiClientError) {
    switch (e.code) {
      case "NOT_FOUND":
        return { kind: "error", title: "Transfer not found", message: "It may have expired or never existed." };
      case "GONE":
        return e.status === 451
          ? { kind: "error", title: "Takedown", message: "This transfer was permanently removed following an abuse report." }
          : { kind: "error", title: "Expired or burned", message: e.message };
      case "FORBIDDEN":
        return { kind: "error", title: "Access denied", message: "The access token in this link is invalid." };
      case "RATE_LIMITED":
        return { kind: "error", title: "Rate limited", message: "Too many requests. Please wait a minute." };
      case "NETWORK":
        return { kind: "error", title: "Connection error", message: e.message };
      default:
        return { kind: "error", title: "Error", message: e.message };
    }
  }
  return { kind: "error", title: "Decryption error", message: errorMessage(e) };
}

function friendlyDecryptError(e: unknown): string {
  if (isLscError(e, "INTEGRITY_FAILED")) return "Integrity check failed: corrupted ciphertext or wrong key.";
  if (isLscError(e, "LENGTH_MISMATCH") || isLscError(e, "SIZE_MISMATCH") || isLscError(e, "TRAILING_DATA")) {
    return "Container size mismatch. Download aborted.";
  }
  if ((e as Error)?.name === "AbortError") return "Cancelled.";
  return errorMessage(e);
}
